import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { FiRefreshCw, FiRotateCcw, FiSearch, FiTrash2 } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ErrorBoundary from '../components/ErrorBoundary';

const SECTION_PATH_RE =
    /^\/(god|gz|simon|superman|ads|assets|unblock|sbf|sfrt)\/RecentDelete\/?$/i;

function resolveSection(sectionParam, pathname) {
    if (sectionParam) return String(sectionParam).toLowerCase();
    const match = pathname.match(SECTION_PATH_RE);
    return match ? match[1].toLowerCase() : '';
}

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-SG', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const daysUntil = (expiresAt) => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

/**
 * Shared trash UI.
 * - `/main-dashboard/recent-deletes` → all sections
 * - `/:section/RecentDelete` (e.g. /god/RecentDelete, /gz/RecentDelete) → filtered
 */
const RecentDeletesPage = () => {
    const { section: sectionParam } = useParams();
    const location = useLocation();
    const section = resolveSection(sectionParam, location.pathname);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [entityType, setEntityType] = useState('');
    const [entityTypes, setEntityTypes] = useState([]);
    const [entityTypeLabels, setEntityTypeLabels] = useState({});
    const [retentionDays, setRetentionDays] = useState(90);
    const [sectionLabel, setSectionLabel] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [confirmMode, setConfirmMode] = useState(null);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
    };

    useEffect(() => {
        setCurrentPage(1);
        setEntityType('');
        setSearchInput('');
        setSearchTerm('');
    }, [section]);

    const fetchTrash = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams({
                page: String(currentPage),
                limit: '20',
            });
            if (searchTerm) params.set('search', searchTerm);
            if (entityType) params.set('entityType', entityType);
            if (section) params.set('section', section);

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/recent-deletes?${params.toString()}`,
                { headers }
            );

            setItems(res.data.items || []);
            setTotalPages(res.data.totalPages || 1);
            setTotal(res.data.total || 0);
            setEntityTypes(res.data.entityTypes || []);
            setEntityTypeLabels(res.data.entityTypeLabels || {});
            if (res.data.retentionDays) setRetentionDays(res.data.retentionDays);
            setSectionLabel(res.data.sectionLabel || (section ? section.toUpperCase() : ''));
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load trash');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, entityType, section]);

    useEffect(() => {
        fetchTrash();
    }, [fetchTrash]);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const openConfirm = (item, mode) => {
        setSelected(item);
        setConfirmMode(mode);
    };

    const closeConfirm = () => {
        setSelected(null);
        setConfirmMode(null);
    };

    const runAction = async () => {
        if (!selected || !confirmMode) return;
        try {
            setActionLoading(true);
            setError('');
            if (confirmMode === 'restore') {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/recent-deletes/${selected._id}/restore`,
                    {},
                    { headers }
                );
            } else {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/recent-deletes/${selected._id}`,
                    { headers }
                );
            }
            setSelected(null);
            setConfirmMode(null);
            await fetchTrash();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Action failed');
        } finally {
            setActionLoading(false);
        }
    };

    const typeLabel = (type) => entityTypeLabels[type] || type;
    const title = sectionLabel
        ? `${sectionLabel} Recent Deletes`
        : 'Recent Deletes (All Projects)';

    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="Recent Deletes Error"
                fallbackDescription="Could not load the trash page."
            >
                <div className="space-y-6 w-full max-w-none px-8 py-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                                <p className="text-gray-600 mt-1 text-sm">
                                    Soft-deleted {sectionLabel || 'ERP'} records stay here for {retentionDays} days.
                                    Restore anytime before expiry, or permanently delete sooner.
                                    {section
                                        ? ' Showing only this project’s trash.'
                                        : ' Showing trash from every project.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={fetchTrash}
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100"
                            >
                                <FiRefreshCw className="w-4 h-4" />
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-4">
                        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                                <div className="relative">
                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        placeholder="Label, type, deleted by…"
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <div className="w-56">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                                <select
                                    value={entityType}
                                    onChange={(e) => {
                                        setCurrentPage(1);
                                        setEntityType(e.target.value);
                                    }}
                                    className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All types</option>
                                    {entityTypes.map((t) => (
                                        <option key={t} value={t}>
                                            {typeLabel(t)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                            >
                                Search
                            </button>
                        </form>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                        {loading ? (
                            <div className="text-center py-10 text-gray-500">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">Trash is empty.</div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {section === 'god' ? 'Summary' : 'Item'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Deleted
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Expires
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            By
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {items.map((item) => {
                                        const days = daysUntil(item.expiresAt);
                                        return (
                                            <tr key={item._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {typeLabel(item.entityType)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    <div className="font-medium">
                                                        {item.displayLabel || (section === 'god' ? typeLabel(item.entityType) : item.originalId)}
                                                    </div>
                                                    {(item.related || []).length > 0 && (
                                                        <div className="text-xs text-gray-500 mt-0.5">
                                                            +{item.related.length} related
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {formatDate(item.deletedAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    <div>{formatDate(item.expiresAt)}</div>
                                                    {days != null && (
                                                        <div
                                                            className={`text-xs mt-0.5 ${
                                                                days <= 7 ? 'text-red-600' : 'text-gray-400'
                                                            }`}
                                                        >
                                                            {days <= 0 ? 'Expiring soon' : `${days}d left`}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {item.deletedBy || '—'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => openConfirm(item, 'restore')}
                                                            className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full"
                                                            title="Restore"
                                                        >
                                                            <FiRotateCcw className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => openConfirm(item, 'purge')}
                                                            className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full"
                                                            title="Delete forever"
                                                        >
                                                            <FiTrash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {!loading && total > 0 && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">{total} item{total === 1 ? '' : 's'} in trash</p>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>

                {confirmMode === 'restore' && selected && (
                    <DeleteConfirmationModal
                        title="Restore item?"
                        message="This will put the record back into its original collection."
                        itemName={selected.displayLabel}
                        confirmLabel="Restore"
                        confirmClassName="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300"
                        isLoading={actionLoading}
                        loadingLabel="Restoring..."
                        onConfirm={runAction}
                        onCancel={closeConfirm}
                    />
                )}

                {confirmMode === 'purge' && selected && (
                    <DeleteConfirmationModal
                        title="Delete forever?"
                        message="This permanently removes the trash entry and any linked R2 files. This cannot be undone."
                        itemName={selected.displayLabel}
                        confirmLabel="Delete forever"
                        isLoading={actionLoading}
                        onConfirm={runAction}
                        onCancel={closeConfirm}
                    />
                )}
            </ErrorBoundary>
        </Layout>
    );
};

export default RecentDeletesPage;
